import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import style from '../../Styles/Order/Customer.module.css';
import Enum from '../../Common/Enum/Enum';
import ModalMessage from '../../Components/Html/ModalMessage';
import { Form, Dropdown } from 'react-bootstrap';
import { ApiCallerConnection } from '../../Api/ApiCallerConnection';

const Customer = forwardRef((props, ref) => {

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const [geoState, setGeoState] = useState({
    loading: false,
    coordinates: null,
    error: null
  });

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    telephoneNumber1: '',
    telephoneNumber2: '',
    nit: '',
    email: '',
    addressDescription: '',
    comments: ''
  });

  const [locationData, setLocationData] = useState({
    selectedDepartment: '',
    selectedDepartmentId: null,
    selectedMunicipio: '',
    selectedMunicipioId: null,
    selectedZone: null,
    selectedZoneId: null,
    selectedAvenue: null,
    selectedAvenueId: null,
    selectedStreet: null,
    selectedStreetId: null,
    selectedPaymentMethodId: null,
    departmentos: [],
    municipios: [],
    zones: [],
    avenues: [],
    streets: [],
    paymentMethods: []
  });

  const [apiState, setApiState] = useState({
    loading: true,
    error: null,
    apiData: null,
    apiPaymentMethods: null
  });

  const handleGetLocation = useCallback(() => {

    if (!navigator.geolocation) {
      setGeoState({ loading: false, coordinates: null, error: 'Geolocalización no soportada' });
      setModalMessage('La geolocalización no es soportada por este navegador.');
      setShowModal(true);
      return;
    }
  
    setGeoState({ loading: true, coordinates: null, error: null });
  
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
        setGeoState({ loading: false, coordinates: coords, error: null });
      },
      (error) => {
        let errorMsg = 'Error al obtener ubicación';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = 'Verifique los persmisos de ubicación';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = 'Ubicación no disponible';
            break;
          case error.TIMEOUT:
            errorMsg = 'Tiempo de espera agotado';
            break;
          default:
            break;
        }
        setGeoState({ loading: false, coordinates: null, error: errorMsg });
        setModalMessage(errorMsg);
        setShowModal(true);
      },
      {
        enableHighAccuracy: true,
        timeout: 180000,
        maximumAge: 0
      }
    );
  }, []);

  const handleCancelModal = () => {
    setShowModal(false);
  };

  const handleConfirmOrder = () => {
    setShowModal(false);
  };

  const updateFormField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateLocationField = useCallback((field, value) => {
    setLocationData(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateApiState = useCallback((updates) => {
    setApiState(prev => ({ ...prev, ...updates }));
  }, []);

  const handleDepartmentSelect = useCallback((departmentId, departmentName, municipiosData) => {
    setLocationData(prev => ({
      ...prev,
      selectedDepartment: departmentName,
      selectedDepartmentId: departmentId,
      municipios: municipiosData || [],
      selectedMunicipio: '',
      selectedMunicipioId: null, 
      selectedZone: null,
      selectedZoneId: null,
      selectedAvenue: null,
      selectedAvenueId: null,
      selectedStreet: null,
      selectedStreetId: null,
      zones: [],
      avenues: [],
      streets: []
    }));
  }, []);

  const handleMunicipioSelect = useCallback((municipioId, municipioName, cadastreData) => {
  
    const cadastre = cadastreData?.[0]; 
    const zones = cadastre?.zones || [];
    const avenues = cadastre?.avenues || [];
    const streets = cadastre?.streets || [];

    setLocationData(prev => ({
      ...prev,
      selectedMunicipio: municipioName,
      selectedMunicipioId: municipioId,      
      selectedZone: null,
      selectedZoneId: null,
      selectedAvenue: null,
      selectedAvenueId: null,
      selectedStreet: null,
      selectedStreetId: null,      
      zones: zones,
      avenues: avenues,
      streets: streets
    }));
  }, []);

  const handleZoneSelect = useCallback((zoneId, zoneName) => {
    setLocationData(prev => ({
      ...prev,
      selectedZone: zoneName,
      selectedZoneId: zoneId
    }));
  }, []);

  const handleAvenueSelect = useCallback((avenueId, avenueName) => {
    setLocationData(prev => ({
      ...prev,
      selectedAvenue: avenueName,
      selectedAvenueId: avenueId
    }));
  }, []);

  const handleStreetSelect = useCallback((streetId, streetName) => {
    setLocationData(prev => ({
      ...prev,
      selectedStreet: streetName,
      selectedStreetId: streetId
    }));
  }, []);

  const handlePaymentSelect = useCallback((paymentId, paymentDescription) => {
    setLocationData(prev => ({
      ...prev,
      selectedPaymentMethodId: paymentId
    }));
  }, []);

  const customerData = useMemo(() => ({
    ...formData,
    idDepartamento: locationData.selectedDepartmentId,
    idMunicipio: locationData.selectedMunicipioId,
    idZone: locationData.selectedZoneId,
    idAvenue: locationData.selectedAvenueId,
    idStreet: locationData.selectedStreetId,
    zone: locationData.selectedZone,
    street: locationData.selectedStreet,
    departamento: locationData.selectedDepartment,
    municipio: locationData.selectedMunicipio,
    avenue: locationData.selectedAvenue,
    idPaymentMethod: locationData.selectedPaymentMethodId,
    ubicationcoordenates: geoState.coordinates
    ? `${geoState.coordinates.latitude},${geoState.coordinates.longitude}`
    : null
  }), [formData, locationData.selectedDepartmentId, locationData.selectedMunicipioId,
    locationData.selectedZone, locationData.selectedAvenue, locationData.selectedStreet,
    locationData.selectedPaymentMethodId, locationData.selectedDepartment, locationData.selectedMunicipio,
    locationData.selectedZoneId, locationData.selectedAvenueId, locationData.selectedStreetId,geoState.coordinates]);


  useImperativeHandle(ref, () => ({
    getCustomerData: () => customerData,
    validateFields: () => {
      const requiredFields = [
        { name: 'Nombre', value: formData.firstName },
        { name: 'Apellido', value: formData.lastName },
        { name: 'Teléfono', value: formData.telephoneNumber1 },
        { name: 'Departamento', value: locationData.selectedDepartmentId },
        { name: 'Municipio', value: locationData.selectedMunicipioId },
        { name: 'Zona', value: locationData.selectedZone },
        { name: 'Avenida', value: locationData.selectedAvenue },
        { name: 'Calle', value: locationData.selectedStreet },
        { name: 'Forma de pago', value: locationData.selectedPaymentMethodId },
        { name: 'Ubicación', value: customerData.ubicationcoordenates }
      ];

      const emptyFields = requiredFields.filter(field => !field.value);

      if (emptyFields.length > 0) {
        const fieldNames = emptyFields.map(f => f.name).join(', ');
        setModalMessage(`Por favor complete los siguientes campos: ${fieldNames}`);
        setShowModal(true);
        return false;
      }
      return true;
    }
  }), [customerData, formData, locationData.selectedDepartmentId, locationData.selectedMunicipioId,
    locationData.selectedZone, locationData.selectedAvenue, locationData.selectedStreet,
    locationData.selectedPaymentMethodId]);

  useEffect(() => {
    if (apiState.apiData?.departamentos) {
      setLocationData(prev => ({
        ...prev,
        departmentos: apiState.apiData.departamentos
      }));
      setApiState(prev => ({ ...prev, loading: false }));
    }
  }, [apiState.apiData]);

  useEffect(() => {
    if (apiState?.apiPaymentMethods?.listOfPaymentsMethods) {
      setLocationData(prev => ({
        ...prev,
        paymentMethods: apiState.apiPaymentMethods.listOfPaymentsMethods
      }));
      setApiState(prev => ({ ...prev, loading: false }));
    }
  }, [apiState.apiPaymentMethods]);

  const FormInput = useCallback(({ placeholder, value, field, optional = false ,isnumeric=false}) => (
    <Form.Control

      as={isnumeric? "input" : "textarea"}
      type={isnumeric ? "number" : "text"}
      placeholder={optional ? `${placeholder} (opcional)` : placeholder}
      className={`${style.personalInput} ${style.commonInput}`}
      value={value}
      onChange={(e) => updateFormField(field, e.target.value)}
      translate="no"
      id={field}
    />
  ), [updateFormField]);


  const DepartmentDropdown = useMemo(() => {
    const { loading, error } = apiState;
    const { selectedDepartment, departmentos } = locationData;

    if (loading) {
      return (
        <Dropdown>
          <Dropdown.Toggle className={`${style.personalInput} ${style.commonInput}`} variant="light" disabled>
            Cargando Departamentos...
          </Dropdown.Toggle>
        </Dropdown>
      );
    }

    if (error) {
      return (
        <Dropdown>
          <Dropdown.Toggle className={`${style.personalInput} ${style.commonInput}`} variant="light" disabled>
            Error al cargar
          </Dropdown.Toggle>
        </Dropdown>
      );
    }

    return (
      <Dropdown onSelect={(eventKey) => {
        const selected = departmentos.find(d => d.iddepartamento.toString() === eventKey);
        if (selected) {
          handleDepartmentSelect(selected.iddepartamento, selected.description, selected.municipios);
        }
      }}>
        <Dropdown.Toggle className={` ${style.commonDropDownDepart}`} variant="light">
          {selectedDepartment || 'Departamento'}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {departmentos.map((department) => (
            <Dropdown.Item key={department.iddepartamento} eventKey={department.iddepartamento.toString()}>
              {department.description}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  }, [apiState, locationData.selectedDepartment, locationData.departmentos, handleDepartmentSelect]);


  const MunicipioDropdown = useMemo(() => {
    const { selectedDepartment, selectedMunicipio, municipios } = locationData;

    return (
      <Dropdown onSelect={(eventKey) => {
        const selected = municipios.find(m => m.idmunicipio.toString() === eventKey);
        if (selected) {          
          handleMunicipioSelect(selected.idmunicipio, selected.description, selected.cadastre);
        }
      }}>
        <Dropdown.Toggle className={style.commonDropDownDepart} variant="light" disabled={!selectedDepartment || municipios.length === 0}>
          {selectedMunicipio || 'Municipio'}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {municipios.length > 0 ? (
            municipios.map((municipio) => (
              <Dropdown.Item key={municipio.idmunicipio} eventKey={municipio.idmunicipio.toString()}>
                {municipio.description}
              </Dropdown.Item>
            ))
          ) : (
            <Dropdown.Item disabled>Seleccione un departamento primero</Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  }, [locationData.selectedDepartment, locationData.selectedMunicipio, locationData.municipios, handleMunicipioSelect]);


  const CadastreDropdown = useMemo(() => {
    const { loading, error } = apiState;
    const { selectedMunicipioId, selectedZone, selectedAvenue, selectedStreet, zones, avenues, streets } = locationData;
    
    if (loading) {
      return (
        <>
          <Dropdown>
            <Dropdown.Toggle className={`${style.personalInput} ${style.commonInput}`} variant="light" disabled>
              Cargando zonas...
            </Dropdown.Toggle>
          </Dropdown>
          <Dropdown>
            <Dropdown.Toggle className={`${style.personalInput} ${style.commonInput}`} variant="light" disabled>
              Cargando avenidas...
            </Dropdown.Toggle>
          </Dropdown>
          <Dropdown>
            <Dropdown.Toggle className={`${style.personalInput} ${style.commonInput}`} variant="light" disabled>
              Cargando calles...
            </Dropdown.Toggle>
          </Dropdown>          
        </>
      );
    }

    if (error) {
      return (
        <>
          <Dropdown>
            <Dropdown.Toggle className={`${style.personalInput} ${style.commonInput}`} variant="light" disabled>
              Error al cargar
            </Dropdown.Toggle>
          </Dropdown>
          <Dropdown>
            <Dropdown.Toggle className={`${style.personalInput} ${style.commonInput}`} variant="light" disabled>
              Error al cargar
            </Dropdown.Toggle>
          </Dropdown>
          <Dropdown>
            <Dropdown.Toggle className={`${style.personalInput} ${style.commonInput}`} variant="light" disabled>
              Error al cargar
            </Dropdown.Toggle>
          </Dropdown>
        </>
      );
    }

    if (!selectedMunicipioId) {
      return (
        <div className={style.cadastreContainer}>
          <Dropdown>
            <Dropdown.Toggle className={style.commonDropDown} variant="light" disabled>
              Calle
            </Dropdown.Toggle>
          </Dropdown>
          <Dropdown>
            <Dropdown.Toggle className={style.commonDropDown} variant="light" disabled>
              Ave.
            </Dropdown.Toggle>
          </Dropdown>
          <Dropdown>
            <Dropdown.Toggle className={style.commonDropDown} variant="light" disabled>
              Zona
            </Dropdown.Toggle>
          </Dropdown>
          <Dropdown>
            <Dropdown.Toggle className={style.commonDropDown} variant="light" disabled>
              Casa
            </Dropdown.Toggle>
          </Dropdown>
        </div>
      );
    }

    return (
      <div className={style.cadastreContainer}>
        <Dropdown onSelect={(eventKey) => {
          const selected = streets?.find(s => s.toString() === eventKey);
          if (selected) {
            handleStreetSelect(selected, `Calle ${selected}`);
          }
        }}>
          <Dropdown.Toggle className={style.commonDropDown} variant="light" disabled={streets.length === 0}>
            {selectedStreet || 'Calle'}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {streets.length > 0 ? (
              streets?.map((street) => (
                <Dropdown.Item key={street} eventKey={street.toString()}>
                  Calle {street}
                </Dropdown.Item>
              ))
            ) : (
              <Dropdown.Item disabled>No hay calles disponibles</Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>


        <Dropdown onSelect={(eventKey) => {
          const selected = avenues?.find(a => a.toString() === eventKey);
          if (selected) {
            handleAvenueSelect(selected, `Ave. ${selected}`);
          }
        }}>
          <Dropdown.Toggle className={style.commonDropDown} variant="light" disabled={avenues.length === 0}>
            {selectedAvenue || 'Ave.'}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {avenues.length > 0 ? (
              avenues?.map((avenue) => (
                <Dropdown.Item key={avenue} eventKey={avenue.toString()}>
                  Avenida {avenue}
                </Dropdown.Item>
              ))
            ) : (
              <Dropdown.Item disabled>No hay avenidas disponibles</Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>


        <Dropdown onSelect={(eventKey) => {
          const selected = zones.find(z => z.toString() === eventKey);
          if (selected) {
            handleZoneSelect(selected, `z. ${selected}`);
          }
        }}>
          <Dropdown.Toggle className={style.commonDropDown} variant="light" disabled={zones.length === 0}>
            {selectedZone || 'z.'}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {zones.length > 0 ? (
              zones.map((zone) => (
                <Dropdown.Item key={zone} eventKey={zone.toString()}>
                  Zona {zone}
                </Dropdown.Item>
              ))
            ) : (
              <Dropdown.Item disabled>No hay zonas disponibles</Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>

      </div>
    );
  }, [apiState, locationData.selectedMunicipioId, locationData.selectedZone, locationData.selectedAvenue,
    locationData.selectedStreet, locationData.zones, locationData.avenues, locationData.streets,
    handleZoneSelect, handleAvenueSelect, handleStreetSelect]);


  const PaymentMethods = useMemo(() => {
    const { selectedPaymentMethodId, paymentMethods } = locationData;

    if (!paymentMethods || paymentMethods.length === 0) {
      return (
        <Dropdown>
          <Dropdown.Toggle className={style.commonDropDown} variant="light">
            Forma de pago
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item disabled>No hay métodos de pago disponibles</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      );
    }

    return (
      <Dropdown onSelect={(eventKey) => {
        const selected = paymentMethods.find(m => m.id?.toString() === eventKey);
        if (selected) {
          handlePaymentSelect(selected.id, selected.description);
        }
      }}>
        <Dropdown.Toggle className={style.commonDropDownDepart} variant="light">
          {selectedPaymentMethodId ?
            paymentMethods.find(m => m.id === selectedPaymentMethodId)?.description || 'Forma de pago'
            : 'Forma de pago'
          }
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {paymentMethods.map((payment) => (
            <Dropdown.Item
              key={payment.id}
              eventKey={payment.id?.toString()}
            >
              {payment.description}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  }, [locationData.paymentMethods, locationData.selectedPaymentMethodId, handlePaymentSelect]);

  const geolocation = useMemo(() => {
    return (
      <div className={style.geoLocationContainer}>
      <button type="button" onClick={handleGetLocation} disabled={geoState.loading} className={`${style.geoLocationButton} ${style.personalInput}`}>
        {geoState.loading ? 'Obteniendo ubicación...' : geoState.coordinates ? '✓ Ubicación obtenida': '📍 Enviar mi ubicación'}
      </button>
      {/* {geoState.coordinates && (
        <small className={style.geoCoords}>
          Lat: {geoState.coordinates.latitude.toFixed(6)}, Lng: {geoState.coordinates.longitude.toFixed(6)}
        </small>
      )} */}
    </div>
    );
  })

  return (
    <div className={style.Customer}>
      <div className={style.headerCustomer}>
        <h2 translate="no"> Datos para la entrega:</h2>
      </div>
      <div className={style.mainBox}>
        <div className={style.bodyCustomer}>
          <div className={`${style.personalInfoSection} ${style.Section}`}>
            <div className={style.addresseTitles}>
              <p translate="no"> Ingresa estos datos :</p>
            </div>
            <FormInput placeholder="Nombre" value={formData.firstName} field="firstName" />
            <FormInput placeholder="Apellido" value={formData.lastName} field="lastName" />
            <FormInput placeholder="Teléfono" value={formData.telephoneNumber1} field="telephoneNumber1" isnumeric />
            <FormInput placeholder="Nit/CF" value={formData.nit} field="nit" optional />
            <FormInput placeholder="Email" value={formData.email} field="email" optional />
          </div>

          <div className={`${style.addressInfoSection} ${style.Section}`}>
            <div className={style.addresseTitles}>
              <p translate="no"> Tu dirección por favor :</p>
            </div>

            <ApiCallerConnection method={process.env.REACT_APP_API_POST} url={process.env.REACT_APP_API_GETDEPARTAMENTOS} useAuth={false}>
              {(response) => {
                if (response.dataResponse?.departamentos && !apiState.apiData) {
                  setTimeout(() => updateApiState({ apiData: response.dataResponse }), 0);
                }
                if (response.error && !apiState.error) {
                  setTimeout(() => updateApiState({ error: response.error }), 0);
                }
                return DepartmentDropdown;
              }}
            </ApiCallerConnection>

            {MunicipioDropdown}
            {CadastreDropdown}
            {geolocation}
                      
            <FormInput  placeholder="Referencia de dirección" value={formData.addressDescription} field="addressDescription" />

            <ApiCallerConnection method={process.env.REACT_APP_API_POST} url={process.env.REACT_APP_API_POST_PAYMENTMETHOD} useAuth={false}>
              {(response) => {
                if (response.dataResponse?.listOfPaymentsMethods) {
                  setTimeout(() => updateApiState({ apiPaymentMethods: response.dataResponse }), 0);
                }
                if (response.error && !apiState.error) {
                  setTimeout(() => updateApiState({ error: response.error }), 0);
                }
                return PaymentMethods;
              }}
            </ApiCallerConnection>
          </div>
        </div>
      </div>

      {showModal && (
        <ModalMessage
          show={showModal}
          onCancel={handleCancelModal}
          menuItemName={modalMessage}
          quantity={0}
          typeOfMessage={Enum.VALIDATIONORDEN}
        />
      )}

    </div>
  );
});

export default Customer;